import { useEffect, useRef } from "react";

/**
 * Auto-lock a stateful component after a period of inactivity.
 *
 * Usage:
 *   useIdleLock({ enabled: unlocked, timeoutMs: 5*60*1000, onLock: () => setUnlocked(false) });
 *
 * The hook listens to a small set of user events on the document and resets a
 * timer. When enabled=false the timer is cleared. When enabled=true, it starts.
 */
const DEFAULT_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
  "visibilitychange",
];

export function useIdleLock({ enabled, timeoutMs = 5 * 60 * 1000, onLock, onWarn, warnBeforeMs = 30 * 1000 }) {
  const timerRef = useRef(null);
  const warnRef = useRef(null);
  const onLockRef = useRef(onLock);
  const onWarnRef = useRef(onWarn);

  useEffect(() => { onLockRef.current = onLock; }, [onLock]);
  useEffect(() => { onWarnRef.current = onWarn; }, [onWarn]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (warnRef.current) { clearTimeout(warnRef.current); warnRef.current = null; }
      return undefined;
    }

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
      if (onWarnRef.current && warnBeforeMs > 0 && warnBeforeMs < timeoutMs) {
        warnRef.current = setTimeout(() => onWarnRef.current?.(), timeoutMs - warnBeforeMs);
      }
      timerRef.current = setTimeout(() => {
        onLockRef.current?.();
      }, timeoutMs);
    };

    reset();

    const handler = () => reset();
    DEFAULT_EVENTS.forEach((e) => window.addEventListener(e, handler, { passive: true }));

    return () => {
      DEFAULT_EVENTS.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
      timerRef.current = null;
      warnRef.current = null;
    };
  }, [enabled, timeoutMs, warnBeforeMs]);
}
